import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
// Store
import { IRootState } from '../../../redux';
// Components
import { Checkbox, Image, Loader } from '../..';
import MenuBarDropdownList from '../MenuBarDropdownList/MenuBarDropdownList';
import ChevronDownIcon from '../../../assets/images/svg/chevrondownicon';
// Assets
import images from '../../../assets/images/images';
import styles from './../menubar.module.scss';
// Interfaces
import { SelectAllItems, MenuBarButtonType } from '../../../enums';
// Helpers
import { isMediaSizeLessThanMD } from '../../../helpers/layout';
import { isTouchScreen } from '../../../helpers/browser';
import KeyboardHandler from '../../../helpers/keyboardHandler';

interface IMenuBarButtonProps extends WrappedComponentProps {
    buttonType: MenuBarButtonType;
    onButtonAction?: (action: MenuBarButtonType) => void;
    onItemSelected: (type: MenuBarButtonType, action: string, actionValue: string) => void;
    buttonEnabled: boolean;
    useButtonLoader: boolean;
    useImage: boolean;
    showEndBorder?: boolean;
    transparentBackGround?: boolean;
}

interface IMenuBarButtonState {
    showDropDownList: boolean;
    selectAll: boolean;
    indeterminate?: boolean;
    showButtonPreloader: boolean;
    dropDownListKeyEvent: React.KeyboardEvent;
}

class MenuBarButton extends React.Component<ReduxType, IMenuBarButtonState> {
    private buttonRef = React.createRef<HTMLDivElement>();
    keyboardHandlerInstance = new KeyboardHandler();

    constructor(props: ReduxType) {
        super(props);
        this.state = {
            showDropDownList: false,
            showButtonPreloader: false,
            selectAll: this.getSelectAllState(),
            indeterminate: this.getIndeterminateState(),
            dropDownListKeyEvent: null,
        };
    }

    componentDidMount(): void {
        this.setupKeyboardHandler();
    }

    componentDidUpdate(prevProps: ReduxType): void {
        if (
            (this.props.buttonType === MenuBarButtonType.SELECT_ALL &&
                prevProps.selectedDocuments !== this.props.selectedDocuments) ||
            prevProps.mediaSize !== this.props.mediaSize
        ) {
            if (this.updateCheckBoxWhenNothingSelected()) return;
            if (this.updateCheckBoxWhenAllSelected(prevProps)) return;
            this.updateCheckBoxWhenPartialSelected(prevProps);
        }
    }

    setDropDownListKeyEvent = (keyEvent: React.KeyboardEvent, data?: any): void => {
        keyEvent.persist();
        this.setState({ dropDownListKeyEvent: keyEvent });
    };

    handleEnterAndSpaceKeys = (keyEvent: React.KeyboardEvent): void => {
        if (!this.props.buttonEnabled) {
            return;
        }

        if (this.state.showDropDownList) {
            this.setDropDownListKeyEvent(keyEvent);
        } else {
            this.showPopupMenu(true);
        }
    };

    setupKeyboardHandler = (): void => {
        this.keyboardHandlerInstance.addHandlers({
            enter: this.handleEnterAndSpaceKeys,
            esc: () => this.showPopupMenu(false),
            space: this.handleEnterAndSpaceKeys,
            up: (event) => (event.altKey ? this.showPopupMenu(false) : this.setDropDownListKeyEvent(event)),
            down: (event) => this.setDropDownListKeyEvent(event),
        });
    };

    updateCheckBoxWhenNothingSelected = () => {
        if (!this.props.selectedDocuments || this.props.selectedDocuments.length === 0) {
            if (this.state.selectAll || this.state.indeterminate) {
                this.setState({ indeterminate: false, selectAll: false });
            }
            return true;
        }

        return false;
    };

    updateCheckBoxWhenAllSelected = (prevProps: ReduxType): boolean => {
        if (
            this.props.selectedDocuments.length > 0 &&
            this.props.selectedDocuments.length !== prevProps.selectedDocuments.length
        ) {
            const documentsShownList = this.props.documents.slice(0, this.props.currentShownDocumentsCount);
            if (this.props.selectedDocuments.length === documentsShownList.length) {
                if (!this.state.selectAll) {
                    this.setState({ indeterminate: false, selectAll: true });
                } else {
                    this.setState({ indeterminate: false });
                }
                return true;
            }
        }

        return false;
    };

    updateCheckBoxWhenPartialSelected = (prevProps: ReduxType): void => {
        if (
            this.props.selectedDocuments.length > 0 &&
            this.props.selectedDocuments.length !== prevProps.selectedDocuments.length
        ) {
            if (!this.state.indeterminate) {
                this.setState({ indeterminate: true, selectAll: false });
            }
        }
    };

    onChange = (): void => {
        this.setState(
            { indeterminate: false, selectAll: this.state.indeterminate ? true : !this.state.selectAll },
            () => {
                if (this.props.onItemSelected) {
                    this.props.onItemSelected(
                        this.props.buttonType,
                        this.state.selectAll ? SelectAllItems.CHECK_ALL : SelectAllItems.UNCHECK_ALL,
                        null
                    );
                }
            }
        );
    };

    onButtonClickHandler = (type: MenuBarButtonType): void => {
        if (!this.props.buttonEnabled) {
            return;
        }

        this.setState({ showButtonPreloader: this.props.useButtonLoader });
        if (this.props.onButtonAction) {
            this.props.onButtonAction(type);
        }

        if (this.props.useButtonLoader) {
            setTimeout(() => {
                this.setState({ showButtonPreloader: false });
            }, 2000);
        }
    };

    changeShowDropDownList = (type: MenuBarButtonType, event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        if (this.props.buttonEnabled) {
            this.showPopupMenu(!this.state.showDropDownList);
        }
    };

    handleClickOutside = (target: Element): void => {
        if (this.state.showDropDownList && this.buttonRef.current && this.buttonRef.current.contains(target)) {
            return;
        }

        this.setState({ showDropDownList: false });
    };

    getSelectAllState = (): boolean => {
        const { selectedDocuments, documents } = this.props;
        const documentsShownList = documents.slice(0, this.props.currentShownDocumentsCount);
        return selectedDocuments.length > 0 && selectedDocuments.length === documentsShownList.length;
    };

    getIndeterminateState = (): boolean => {
        const { selectedDocuments, documents } = this.props;
        const documentsShownList = documents.slice(0, this.props.currentShownDocumentsCount);
        return selectedDocuments.length > 0 && selectedDocuments.length < documentsShownList.length;
    };

    showPopupMenu = (show: boolean): void => {
        this.setState({ showDropDownList: show });
    };

    checkBlurState = (event: React.FocusEvent): void => {
        if (event.relatedTarget) {
            const node = event.relatedTarget as Element;
            if (node) {
                this.handleClickOutside(node);
            }
        }
    };

    handleItemSelected = (type: MenuBarButtonType, action: string, actionValue: string): void => {
        this.showPopupMenu(false);
        if (this.props.buttonType === MenuBarButtonType.SELECT_ALL && action === SelectAllItems.CHECK_ALL) {
            this.setState({ selectAll: true });
        }

        if (this.props.onItemSelected) {
            this.props.onItemSelected(type, action, actionValue);
        }
    };

    getSelectAllButton = (): JSX.Element => {
        const notTouchScreen = !isTouchScreen();
        return (
            <div
                ref={this.buttonRef}
                className={[
                    styles.selectAllCheckbox,
                    styles.actionItem,
                    styles.closingBorder,
                    this.props.buttonEnabled ? styles.Active : styles.Inactive,
                    'd-flex align-items-center',
                ].join(' ')}
                onBlur={this.checkBlurState}
            >
                <Checkbox
                    disabled={!this.props.buttonEnabled}
                    className="px-2 d-flex align-items-center"
                    name="selectAllCheckbox"
                    checked={this.state.selectAll}
                    indeterminate={this.state.indeterminate}
                    onChange={this.onChange}
                    aria-label={this.props.intl.formatMessage({ id: 'SELECT_ALL_MAILS' })}
                    title={this.props.intl.formatMessage({ id: 'SELECT_ALL_MAILS' })}
                />
                <div
                    className="d-flex px-2 align-items-center"
                    role="button"
                    aria-label={this.props.intl.formatMessage({ id: 'SELECT_OPTION_FROM_DROPDOWN' })}
                    tabIndex={0}
                    {...(notTouchScreen && { onKeyDown: this.keyboardHandlerInstance.onPress })}
                    onClick={(event) => this.changeShowDropDownList(this.props.buttonType, event)}
                >
                    <div title={this.props.intl.formatMessage({ id: 'SELECT_OPTION_FROM_DROPDOWN' })}>
                        <em className={styles.chevron} />
                    </div>
                </div>
                <MenuBarDropdownList
                    onClickedOutside={this.handleClickOutside}
                    onItemSelected={this.handleItemSelected}
                    show={this.state.showDropDownList}
                    type={this.props.buttonType}
                    keyEvent={this.state.dropDownListKeyEvent}
                />
            </div>
        );
    };

    getButton = (translateKey: string, buttonStyle = ''): JSX.Element => {
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        const title = this.props.intl.formatMessage({ id: translateKey });
        const notTouchScreen = !isTouchScreen();

        return (
            <div
                id={this.props.buttonType}
                className={[
                    'd-flex align-items-center',
                    styles.actionItem,
                    this.props.showEndBorder ? styles.closingBorder : '',
                    this.props.buttonEnabled ? styles.Active : styles.Inactive,
                ].join(' ')}
            >
                <button
                    className={[
                        'd-flex h-100 align-items-center pr-3',
                        styles.clearButtonStyles,
                        this.props.useButtonLoader && this.state.showButtonPreloader
                            ? 'pl-2'
                            : !this.props.useImage
                            ? 'pl-3'
                            : '',
                    ].join(' ')}
                    tabIndex={this.props.buttonEnabled ? 0 : -1}
                    type="button"
                    title={title}
                    disabled={!this.props.buttonEnabled}
                    onClick={() => this.onButtonClickHandler(this.props.buttonType)}
                    {...(notTouchScreen && {
                        onKeyDown: (e) => e.key === 'Enter' && this.onButtonClickHandler(this.props.buttonType),
                    })}
                >
                    {!isSmall ? (
                        <>
                            {this.props.useButtonLoader && this.state.showButtonPreloader ? (
                                <em className="d-flex mr-2">
                                    <Loader show={this.state.showButtonPreloader} size="md" />
                                </em>
                            ) : this.props.useImage ? (
                                <Image
                                    src={images.mailBoxActions}
                                    className={['d-flex mx-2', buttonStyle].join(' ')}
                                    tabIndex={-1}
                                    aria-hidden
                                    alt={translateKey}
                                />
                            ) : null}
                            <FormattedMessage id={translateKey} defaultMessage={translateKey} />
                        </>
                    ) : this.props.useImage ? (
                        <Image
                            src={images.sprite}
                            className={['d-flex mx-2', buttonStyle].join(' ')}
                            tabIndex={-1}
                            alt={translateKey}
                        />
                    ) : null}
                </button>
            </div>
        );
    };

    getChevronDropDownButton = (
        translateKey: string,
        buttonStyle = styles.buttonSmallRound,
        topActionStyle: string,
        rightAligned = false
    ): JSX.Element => {
        const notTouchScreen = !isTouchScreen();
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        return (
            <div
                ref={this.buttonRef}
                className={[
                    styles.mailboxTopActions,
                    styles.actionItem,
                    !this.props.buttonEnabled ? styles.inactive : '',
                    'd-flex align-items-center',
                    styles.sortAndFilter,
                    topActionStyle,
                    isMediaSizeLessThanMD(this.props.mediaSize) ? styles.mobile : '',
                ].join(' ')}
                onClick={(event) => this.changeShowDropDownList(this.props.buttonType, event)}
                onBlur={notTouchScreen && !isSmall ? this.checkBlurState : null}
                {...(notTouchScreen && { onKeyDown: this.keyboardHandlerInstance.onPress })}
            >
                <span
                    className={['text-medium mr-1', !isMediaSizeLessThanMD(this.props.mediaSize) && 'bold'].join(' ')}
                >
                    <FormattedMessage id={translateKey} />
                </span>
                <div
                    role="listbox"
                    id="sortButton"
                    tabIndex={0}
                    aria-label={this.props.intl.formatMessage({ id: translateKey })}
                    className={[
                        styles.actionButton,
                        buttonStyle,
                        'm-0',
                        notTouchScreen ? styles.notTouchLargeSize : '',
                    ].join(' ')}
                    onClick={(event) => this.changeShowDropDownList(this.props.buttonType, event)}
                >
                    <ChevronDownIcon className={styles.chevronIcon} />
                    <MenuBarDropdownList
                        onClickedOutside={this.handleClickOutside}
                        rightAligned={rightAligned}
                        keyEvent={this.state.dropDownListKeyEvent}
                        onItemSelected={this.handleItemSelected}
                        show={this.state.showDropDownList}
                        type={this.props.buttonType}
                    />
                </div>
            </div>
        );
    };

    getDropDownButton = (
        translateKey: string,
        buttonStyle = '',
        rightAligned = false,
        noRightPaddingOnSmall = false
    ): JSX.Element => {
        const notTouchScreen = !isTouchScreen();
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        const title = this.props.intl.formatMessage({ id: translateKey });
        return (
            <div
                id={translateKey}
                ref={this.buttonRef}
                title={title}
                tabIndex={-1}
                className={[
                    styles.actionItem,
                    this.props.showEndBorder ? styles.closingBorder : '',
                    this.props.buttonEnabled ? styles.Active : styles.Inactive,
                    'd-flex align-items-center',
                    this.props.transparentBackGround ? styles.transparentBackground : '',
                ].join(' ')}
                onClick={(event) => this.changeShowDropDownList(this.props.buttonType, event)}
                onBlur={notTouchScreen && !isSmall ? this.checkBlurState : null}
                {...(notTouchScreen && { onKeyDown: this.keyboardHandlerInstance.onPress })}
            >
                <div
                    className={[
                        'd-flex flex-column h-100',
                        rightAligned ? 'align-items-end' : 'align-items-start',
                    ].join(' ')}
                >
                    <div
                        className={[
                            'd-flex h-100 align-items-center',
                            noRightPaddingOnSmall && isSmall ? '' : 'pr-3',
                            this.props.useImage || isSmall ? '' : 'pl-3',
                        ].join(' ')}
                        role="listbox"
                        onClick={(event) => this.changeShowDropDownList(this.props.buttonType, event)}
                        aria-label={title}
                        tabIndex={this.props.buttonEnabled ? 0 : -1}
                    >
                        {this.props.useImage || isSmall ? (
                            <Image
                                src={isSmall ? images.sprite : images.mailBoxActions}
                                className={['d-flex mx-2', buttonStyle].join(' ')}
                                tabIndex={-1}
                                aria-hidden
                                alt={translateKey}
                            />
                        ) : null}
                        {!isSmall ? (
                            <>
                                <FormattedMessage id={translateKey} defaultMessage={translateKey} />
                                <em className={[styles.chevron, 'd-flex pl-2'].join(' ')} tabIndex={-1} />
                            </>
                        ) : null}
                    </div>
                    <MenuBarDropdownList
                        onClickedOutside={this.handleClickOutside}
                        rightAligned={rightAligned}
                        onItemSelected={this.handleItemSelected}
                        show={this.state.showDropDownList}
                        type={this.props.buttonType}
                        keyEvent={this.state.dropDownListKeyEvent}
                    />
                </div>
            </div>
        );
    };

    getNoteText = (): string => {
        if (this.props.selectedDocuments[0]) {
            const mailId = this.props.selectedDocuments[0].id;
            const selectedDocument = this.props.documents.filter((document) => document.id === mailId)[0];
            if (selectedDocument) {
                return !selectedDocument.note ? 'ADD_NOTE' : 'EDIT_NOTE';
            }
        }

        return 'ADD_NOTE';
    };

    getRenderButton = (): JSX.Element => {
        switch (this.props.buttonType) {
            case MenuBarButtonType.SELECT_ALL:
                return this.getSelectAllButton();
            case MenuBarButtonType.MAIL_REPLY:
                return this.getButton('WRITE_REPLY_MESSAGE', styles.mailReply);
            case MenuBarButtonType.MAIL_SEND:
                return this.getButton('WRITE_NEW_MESSAGE', styles.mailSend);
            case MenuBarButtonType.MAIL_FORWARD:
                return this.getButton('FORWARD', styles.mailForward);
            case MenuBarButtonType.MAIL_ARCHIVE:
                return this.getButton('ARCHIVE');
            case MenuBarButtonType.MOVE_TO_FOLDER:
                return this.getDropDownButton('MOVE', styles.mailMove, isMediaSizeLessThanMD(this.props.mediaSize));
            case MenuBarButtonType.MAIL_DELETE:
                return this.getButton('DELETE', styles.mailDelete);
            case MenuBarButtonType.SAVE:
                return this.getButton('DOWNLOAD');
            case MenuBarButtonType.MORE_ACTIONS:
                return this.getDropDownButton(
                    'MORE',
                    isMediaSizeLessThanMD(this.props.mediaSize) ? styles.moreImage : '',
                    true,
                    true
                );
            case MenuBarButtonType.MAIL_PRINT:
                return this.getButton('TEXT_PRINT');
            case MenuBarButtonType.CLOSE:
                return this.getButton('CLOSE');
            case MenuBarButtonType.ADD_EDIT_NOTE:
                return this.getButton(this.getNoteText());
            case MenuBarButtonType.MORE_INFORMATION:
                return this.getButton('INFORMATION');
            case MenuBarButtonType.SORT_FILTER:
                return this.getChevronDropDownButton('FILTER', '', '', true);
            case MenuBarButtonType.ORGANIZATION_CATEGORY:
                return this.getChevronDropDownButton('ORGANIZATION_CATEGORY_FIND_TEXT', '', styles.popupOrderTop, true);
            default:
                return null;
        }
    };

    public render(): JSX.Element {
        return this.getRenderButton();
    }
}

const mapStateToProps = ({ mailbox, menubar, appReadouts }: IRootState) => {
    return {
        selectedDocuments: mailbox.selectedDocuments,
        documents: mailbox.selectedSender == null ? mailbox.documentList : mailbox.filteredDocumentList,
        mediaSize: appReadouts.mediaSize,
        selectedAction: menubar.selectMailItemsAction,
        currentShownDocumentsCount: mailbox.currentShownDocumentsCount,
    };
};

type ReduxType = IMenuBarButtonProps & ReturnType<typeof mapStateToProps>;

export default injectIntl(connect(mapStateToProps)(MenuBarButton));
